import { createContext, useContext, useState, type ReactNode } from 'react';
import { SIGNUP_ROUTES } from '@/api/constants/app.url';
import type { AccountType, SignupState, StepDef } from '@/modules/signup/types';

/**
 * Multi-step signup state. Direct port of the legacy
 * apps/dashboard/src/app/signup/signup-context.tsx — same flow, same
 * step definitions, exported through this module.
 *
 * Why Context here (not TanStack Query):
 *   - Pure UI state, not server state. The funnel isn't refetchable.
 *   - State has to survive 7 page navigations and reset cleanly on exit.
 *   - One provider mounted by the SignupLayout, everything below reads.
 *
 * Owner flow: Cover → Account → Subtype  → Plan → Details → Verify → Onboard
 * Agent flow: Cover → Account → WorkMode → Plan → Details → Verify → Onboard
 */

interface SignupContextValue {
    signup: SignupState;
    update: (patch: Partial<SignupState>) => void;
    reset:  () => void;
}

const INITIAL: SignupState = {
    accountType:    'owner',
    subType:        'developer',
    plan:           'developer-growth',
    billing:        'monthly',
    fullName:       '',
    email:          '',
    phone:          '',
    orgName:        '',
    password:       '',
    pendingSession: null,
};

const SignupContext = createContext<SignupContextValue | null>(null);

export function SignupProvider({ children }: { children: ReactNode }) {
    const [signup, setSignup] = useState<SignupState>(INITIAL);
    const update = (patch: Partial<SignupState>) => setSignup((s) => ({ ...s, ...patch }));
    const reset  = () => setSignup(INITIAL);
    return <SignupContext.Provider value={{ signup, update, reset }}>{children}</SignupContext.Provider>;
}

export function useSignup(): SignupContextValue {
    const ctx = useContext(SignupContext);
    if (!ctx) throw new Error('useSignup must be used inside <SignupProvider>');
    return ctx;
}

export const STEPS_OWNER: StepDef[] = [
    { path: SIGNUP_ROUTES.root,    num: 1, label: 'Cover' },
    { path: SIGNUP_ROUTES.account, num: 2, label: 'Account' },
    { path: SIGNUP_ROUTES.subtype, num: 3, label: 'Subtype' },
    { path: SIGNUP_ROUTES.plan,    num: 4, label: 'Plan' },
    { path: SIGNUP_ROUTES.details, num: 5, label: 'Details' },
    { path: SIGNUP_ROUTES.verify,  num: 6, label: 'Verify' },
    { path: SIGNUP_ROUTES.onboard, num: 7, label: 'Onboard' },
];

export const STEPS_AGENT: StepDef[] = [
    { path: SIGNUP_ROUTES.root,     num: 1, label: 'Cover' },
    { path: SIGNUP_ROUTES.account,  num: 2, label: 'Account' },
    { path: SIGNUP_ROUTES.workMode, num: 3, label: 'Work mode' },
    { path: SIGNUP_ROUTES.plan,     num: 4, label: 'Plan' },
    { path: SIGNUP_ROUTES.details,  num: 5, label: 'Details' },
    { path: SIGNUP_ROUTES.verify,   num: 6, label: 'Verify' },
    { path: SIGNUP_ROUTES.onboard,  num: 7, label: 'Onboard' },
];

export function stepsFor(accountType: AccountType): StepDef[] {
    return accountType === 'agent' ? STEPS_AGENT : STEPS_OWNER;
}
